import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * GlassCard — pattern 21st.dev / Magic
 * 3D perspective tilt on hover + inner glass refraction layer
 * Top highlight line + diagonal gradient overlay
 */
export const GlassCard = ({
  children,
  className = '',
  hover = true,
  delay = 0,
  ...props
}) => {
  return (
    /* ── 21st Magic: perspective wrapper for 3D tilt ── */
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      style={{ perspective: '1000px' }}
      className="w-full"
    >
      <motion.div
        whileHover={hover ? {
          rotateX: -3,
          rotateY: 3,
          y: -6,
          scale: 1.015,
          boxShadow:
            '0 28px 70px rgba(0,0,0,0.6), 0 0 0 1px rgba(79,172,254,0.22), 0 0 36px rgba(79,172,254,0.16)',
        } : {}}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className={cn(
          /* base glass — slightly brighter for less-dark aesthetic */
          'bg-white/[0.06] backdrop-blur-xl',
          'border border-white/[0.10]',
          'shadow-[0_8px_40px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.05)]',
          'rounded-2xl',
          'p-6 sm:p-8 relative overflow-hidden',
          /* make-interfaces-feel-better: specific transition, not transition-all */
          'transition-[background-color,border-color] duration-300',
          hover && 'hover:bg-white/[0.09] hover:border-white/[0.16]',
          /* 21st shine sweep */
          'card-shine-wrapper',
          className
        )}
        /* make-interfaces-feel-better: will-change only on transform (GPU composited) */
        style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
        {...props}
      >
        {/* ── 21st Magic inner glass refraction layer (translate3d depth) ── */}
        {hover && (
          <div
            className="absolute inset-[2px] rounded-[14px] border-t border-l border-white/15 bg-gradient-to-b from-white/[0.06] to-transparent pointer-events-none"
            style={{ transform: 'translate3d(0, 0, 1px)' }}
          />
        )}

        {/* Top highlight — simulates glass refraction */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />

        {/* Bottom edge glow */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#4facfe]/12 to-transparent pointer-events-none" />

        {/* Diagonal ambient gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#4facfe]/[0.03] via-transparent to-[#a855f7]/[0.025] pointer-events-none rounded-2xl" />

        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GlassCard;
